import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AdminLayout from '../../layouts/AdminLayout';
import { AdminService } from '../../services/adminService';

// Trous de disponibilité du service d'exercices : la génération LLM live est abandonnée,
// le service dégrade gracieusement (notion → compétence → domaine). Cette page montre OÙ le
// pool validé est mince, pour piloter la relecture/génération offline.

const WINDOWS = [
  { label: '24 h', hours: 24 },
  { label: '7 j', hours: 168 },
  { label: '30 j', hours: 720 },
];

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString('fr-FR'); } catch { return String(v); }
}

export default function ServingGapsScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(168);
  const [gaps, setGaps] = useState([]);

  const fetchGaps = useCallback(async (h) => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminService.getServingGaps({ hours: h, limit: 300 });
      setGaps(Array.isArray(data?.gaps) ? data.gaps : []);
    } catch (e) {
      setError(e?.message || 'Erreur lors du chargement des trous de service');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGaps(hours); }, [hours, fetchGaps]);

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Trous de disponibilité</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
          Génération live abandonnée : le service dégrade gracieusement (même notion difficulté/cycle
          différents → notion différente même compétence → domaine) et livre quand même. Chaque
          dégradation est tracée ici. « Autre notion » et « Rien servi » sont les vrais manques du
          pool (en tête) ; « Autre échelon » sert la bonne notion à un autre cycle ou une autre
          difficulté — signal faible. « Enfants » ne compte que les trous tracés depuis le 2026-10-12.
        </Typography>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={hours}
          onChange={(_e, v) => { if (v != null) setHours(v); }}
          sx={{ mb: 2 }}
        >
          {WINDOWS.map((w) => (
            <ToggleButton key={w.hours} value={w.hours}>{w.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : gaps.length === 0 ? (
          <Card><CardContent>
            <Typography color="text.secondary">
              Aucun trou de service sur la fenêtre choisie — le pool couvre tout ce qui a été demandé. 🎉
            </Typography>
          </CardContent></Card>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Type de jeu</TableCell>
                  <TableCell>Domaine</TableCell>
                  <TableCell>Compétence</TableCell>
                  <TableCell>Notion</TableCell>
                  <TableCell>Cycle</TableCell>
                  <TableCell>Difficulté</TableCell>
                  <TableCell align="right">Occurrences</TableCell>
                  <TableCell align="right">Autre échelon</TableCell>
                  <TableCell align="right">Autre notion</TableCell>
                  <TableCell align="right">Rien servi</TableCell>
                  <TableCell align="right">Items manquants</TableCell>
                  <TableCell align="right">Enfants</TableCell>
                  <TableCell>Dernier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gaps.map((g, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Chip size="small" label={g.gameType} /></TableCell>
                    <TableCell>{g.domainLabel || g.domain || '—'}</TableCell>
                    <TableCell>{g.competenceLabel || g.competence || '—'}</TableCell>
                    <TableCell>
                      {g.notionLabel || g.notion || '—'}
                      {g.notionLabel && g.notion ? (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({g.notion})
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{g.cycle ?? '—'}</TableCell>
                    <TableCell>{g.difficulty || '—'}</TableCell>
                    <TableCell align="right"><strong>{g.occurrences}</strong></TableCell>
                    <TableCell align="right">{g.autreEchelon}</TableCell>
                    <TableCell align="right">
                      {g.autreNotion > 0
                        ? <Chip size="small" color="warning" label={g.autreNotion} />
                        : 0}
                    </TableCell>
                    <TableCell align="right">
                      {g.rienServi > 0
                        ? <Chip size="small" color="error" label={g.rienServi} />
                        : 0}
                    </TableCell>
                    <TableCell align="right">{g.missingItems}</TableCell>
                    <TableCell align="right">{g.enfants}</TableCell>
                    <TableCell>{fmtDate(g.lastSeen)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </AdminLayout>
  );
}
